import { useEffect, useMemo } from "react"

import { useTransportInfiniteQuery } from "@/transport"

import type { AdminListSimulationDataFramesRequest as ListSimulationDataFramesRequest } from "@/types_api/backend/transport/admin_list_simulation_data_frames_request"
import type {
  AdminListSimulationDataFramesResponse as ListSimulationDataFramesResponse,
  AdminSimulationDataFrameRow as SimulationDataFrameRow,
} from "@/types_api/backend/transport/admin_list_simulation_data_frames_response"

import { buildSimulationDataFrameMarks } from "../utils/simulationDataFrames"

const PAGE_SIZE = 1_000
const MAX_ROWS = 10_000

function getNextRequest(
  page: ListSimulationDataFramesResponse,
  previousRequest: ListSimulationDataFramesRequest | null | undefined,
): ListSimulationDataFramesRequest | null {
  return page.next_cursor && previousRequest
    ? { ...previousRequest, cursor: page.next_cursor }
    : null
}

function groupRowsByTable(pages: ListSimulationDataFramesResponse[]) {
  const rowsByTable = Object.create(null) as Record<
    string,
    SimulationDataFrameRow[]
  >
  let rowCount = 0
  let isTruncated = false

  for (const page of pages) {
    for (const table of page.tables) {
      const remaining = MAX_ROWS - rowCount
      const rows = table.rows.slice(0, remaining)
      isTruncated ||= rows.length < table.rows.length
      rowsByTable[table.name] = [...(rowsByTable[table.name] ?? []), ...rows]
      rowCount += rows.length
    }
  }

  return { rowsByTable, rowCount, isTruncated }
}

export function useSimulationDataFrames({
  simulationOutputId,
  enabled,
}: {
  simulationOutputId: string
  enabled: boolean
}) {
  const request = useMemo<ListSimulationDataFramesRequest>(
    () => ({
      simulation_output_id: simulationOutputId,
      page_size: PAGE_SIZE,
    }),
    [simulationOutputId],
  )
  const query = useTransportInfiniteQuery({
    // Keep the published operation until the canonical listSimulationDataFrames contract is released.
    operation: "listAdminSimulationDataFrames",
    request,
    getNextRequest: (page, _pages, previousRequest) =>
      getNextRequest(page, previousRequest),
    options: {
      enabled,
      abortOnUnmount: true,
      refetchOnWindowFocus: false,
      reportErrors: false,
    },
  })
  const { rowsByTable, rowCount, isTruncated } = useMemo(
    () => groupRowsByTable(query.data?.pages ?? []),
    [query.data],
  )
  const { error, fetchNextPage, hasNextPage, isFetchingNextPage } = query
  const hasCompleteFrameSet =
    Boolean(query.data?.pages.length) &&
    (!query.hasNextPage || rowCount >= MAX_ROWS)

  useEffect(() => {
    if (
      enabled &&
      hasNextPage &&
      !isFetchingNextPage &&
      !error &&
      rowCount < MAX_ROWS
    ) {
      void fetchNextPage()
    }
  }, [enabled, error, fetchNextPage, hasNextPage, isFetchingNextPage, rowCount])

  const marks = useMemo(
    () => buildSimulationDataFrameMarks(rowsByTable),
    [rowsByTable],
  )
  return {
    marks,
    rowsByTable,
    isLoading:
      query.isLoading ||
      (enabled && query.isPending) ||
      query.isFetchingNextPage,
    isTruncated:
      isTruncated || Boolean(query.hasNextPage && rowCount >= MAX_ROWS),
    hasCompleteFrameSet,
    error: query.error,
    refetch: query.refetch,
  }
}
